// Team Outing — a project-wise event planned by a PM. Mirrors the backend Outing entity.

export enum OutingStatus {
  Planning = 1,
  Done = 2,
  Cancelled = 3
}

export enum OutingAttachmentKind {
  Photo = 1,
  Bill = 2
}

export interface OutingAttachment {
  id: string;
  outingId: string;
  kind: OutingAttachmentKind;
  url: string;
  fileName: string;
  contentType?: string;
  uploadedByName?: string;
  uploadedDate: string;
}

// Public (no-login) photos page payload.
export interface PublicOutingPhoto {
  id: string;
  url: string;
  fileName: string;
}

export interface PublicOutingPhotos {
  outingId: string;
  eventName: string;
  projectName: string;
  pmName: string;
  plannedDate: string;
  location?: string;
  photos: PublicOutingPhoto[];
}

export interface OutingParticipant {
  id: string;
  employeeId: string;
  employeeName: string;
  email?: string;
  attended: boolean;
  confirmed: boolean;
  confirmedDate?: string;
  serviceGroupId?: string;
  serviceGroupName?: string;
}

// Public RSVP page view (participant opening their email link).
export interface PublicOutingRsvp {
  employeeName: string;
  eventName: string;
  projectName: string;
  pmName: string;
  plannedDate: string;
  location?: string;
  startTime?: string;
  remarks?: string;
  alreadyConfirmed: boolean;
}

export interface Outing {
  id: string;
  projectId: string;
  projectName: string;
  projectCode?: string;
  pmId: string;
  pmName: string;
  eventName: string;
  plannedDate: string;   // ISO date
  quarter: number;
  year: number;
  status: OutingStatus;
  location?: string;
  startTime?: string;
  budget: number;
  actualSpend?: number;
  remarks?: string;
  photosFolderUrl?: string;
  participants: OutingParticipant[];
}

export interface CreateOuting {
  projectId: string;
  pmId?: string;
  eventName: string;
  plannedDate: string;
  status: OutingStatus;
  location?: string;
  startTime?: string;
  budget: number;
  actualSpend?: number;
  remarks?: string;
  photosFolderUrl?: string;
  participantIds: string[];
}

export interface UpdateOuting extends CreateOuting {
  attendedIds: string[];
}

// Pre-send facts for the "email Marketing" confirmation dialog.
export interface OutingMarketingEmailPreview {
  recipients: string[];
  photoCount: number;
  photosPageUrl: string;
  alreadySent: boolean;
  lastSentDate?: string;
}

// Result of actually re-sending the recap.
export interface OutingMarketingEmailResult {
  sent: boolean;
  recipients: string[];
  photoCount: number;
  photosPageUrl: string;
  message?: string;
}

export interface OutingQuarterReport {
  year: number;
  quarter: number;
  quarterLabel: string;
  outingCount: number;
  completedCount: number;
  totalBudget: number;
  totalActualSpend: number;
  invitedCount: number;
  attendedCount: number;
  participationRate: number;
}

export interface OutingReportSummary {
  totalOutings: number;
  plannedCount: number;
  confirmedCount: number;
  completedCount: number;
  cancelledCount: number;
  totalBudget: number;
  totalActualSpend: number;
  participationRate: number;
  byQuarter: OutingQuarterReport[];
}

// UI helpers
export const OUTING_STATUS_LABELS: Record<OutingStatus, string> = {
  [OutingStatus.Planning]: 'Planning',
  [OutingStatus.Done]: 'Done',
  [OutingStatus.Cancelled]: 'Cancelled'
};
